import React, { useEffect, useRef, useState } from 'react';
import { Activity, Bot, Brain, ChartNoAxesColumn, Check, Database, Eye, FileWarning, Gauge, KeyRound, MessageCircle, PlugZap, RefreshCw, Rocket, Sparkles, ThumbsUp, X, Zap } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { SlackPanel } from '../components/SlackIntegrationGraph';
import { CodeTerminal, SurfaceTab, TabIcon, type SurfaceKey } from './CompanyBrain';
import { MoreThanAnswers } from './GigaForChatGPT2';
const SECTIONS = [{
  id: 'co-worker',
  label: 'Co Worker',
  icon: Bot
}, {
  id: 'company-brain',
  label: 'Company Brain',
  icon: Brain
}, {
  id: 'connected-apps',
  label: 'Connected Apps',
  icon: PlugZap
}] as const;
type SectionId = (typeof SECTIONS)[number]['id'];
const HEADLINE_ROTATION_MS = 4200;
const HEADLINE_ROTATION_OFFSET_MS = 2100;
const BRAIN_SCENARIO_ROTATION_MS = 14000;
const BRAIN_SCENARIOS = [{
  id: 'onboarding-decisions',
  leftPrompt: "Let's wrap up.",
  leftResponse: ['Discussion: the team debated dashboard polish versus reducing first-run setup steps.', 'Decision: ship the guided setup checklist before dashboard polish.', 'Issue: workspace import errors are still blocking some larger teams.', 'Customer signal: trial users like the new reporting, but keep asking for a clearer setup path.', 'Progress: checklist copy and empty states are nearly finished.', 'Metric: activation is up 14% since the reporting update.'],
  rightPrompt: 'Where did we get to on the onboarding project?',
  rightResponse: ['The team discussed dashboard polish versus reducing setup friction.', 'The decision was to ship the guided setup checklist before dashboard polish.'],
  activeKnowledge: ['Discussions', 'Decisions']
}, {
  id: 'onboarding-signals',
  leftPrompt: "Let's wrap up.",
  leftResponse: ['Customer signal: trial users like the new reporting, but keep asking for a clearer setup path.', 'Issue: workspace import errors are still blocking some larger teams.'],
  rightPrompt: 'What are users saying about onboarding?',
  rightResponse: ['Users are positive on reporting, but the repeated signal is that setup still feels unclear.', 'The concrete issue is workspace imports failing for some larger teams.'],
  activeKnowledge: ['Issues', 'Customer signals']
}, {
  id: 'onboarding-progress',
  leftPrompt: "Let's wrap up.",
  leftResponse: ['Progress: checklist copy and empty states are nearly finished.', 'Metric: activation is up 14% since the reporting update.'],
  rightPrompt: 'How are we doing on onboarding?',
  rightResponse: ['Checklist copy and empty states are nearly finished.', 'Activation is up 14%, so the next priority is clearing import errors and getting the checklist live.'],
  activeKnowledge: ['Progress', 'Metrics']
}] as const;
const LEFT_WORKING_STEPS = ['Summarizing conversation', 'Synthesizing takeaways', 'Searching relevant tracks', 'Saving...', 'Saved to Giga'];
const KNOWLEDGE_TYPES = ['Discussions', 'Decisions', 'Issues', 'Customer signals', 'Progress', 'Metrics'];
const KNOWLEDGE_ICONS: Record<string, React.ElementType> = {
  Discussions: MessageCircle,
  Decisions: ThumbsUp,
  Issues: FileWarning,
  'Customer signals': Activity,
  Progress: Gauge,
  Metrics: ChartNoAxesColumn
};
export function Alt() {
  const [activeSection, setActiveSection] = useState<SectionId>('co-worker');
  const [activeSurface, setActiveSurface] = useState<SurfaceKey>('slack');
  const [activeKnowledgeIndex, setActiveKnowledgeIndex] = useState(0);
  const [headlineIndex, setHeadlineIndex] = useState(0);
  const [audienceIndex, setAudienceIndex] = useState(0);
  const [brainVerbIndex, setBrainVerbIndex] = useState(0);
  const navRef = useRef<HTMLDivElement | null>(null);
  const lastScrollYRef = useRef(0);
  const headlinePhrases = ['Go Faster', 'Stay in Control'];
  const audiencePhrases = ['companies', 'founders', 'operators', 'small businesses'];
  const brainVerbs = ['captures', 'understands'];
  const brainScenarioIndex = Math.floor(activeKnowledgeIndex / 2);
  const selectBrainKnowledge = (type: string) => {
    const index = KNOWLEDGE_TYPES.indexOf(type);
    if (index >= 0) setActiveKnowledgeIndex(index);
  };
  useEffect(() => {
    const interval = window.setInterval(() => {
      setHeadlineIndex(current => (current + 1) % headlinePhrases.length);
    }, HEADLINE_ROTATION_MS);
    return () => window.clearInterval(interval);
  }, [headlinePhrases.length]);
  useEffect(() => {
    let interval: number | undefined;
    const timeout = window.setTimeout(() => {
      setAudienceIndex(current => (current + 1) % audiencePhrases.length);
      interval = window.setInterval(() => {
        setAudienceIndex(current => (current + 1) % audiencePhrases.length);
      }, HEADLINE_ROTATION_MS);
    }, HEADLINE_ROTATION_OFFSET_MS);
    return () => {
      window.clearTimeout(timeout);
      if (interval) window.clearInterval(interval);
    };
  }, [audiencePhrases.length]);
  useEffect(() => {
    if (activeSection !== 'company-brain') return;
    const interval = window.setInterval(() => {
      setActiveKnowledgeIndex(current => (current + 1) % KNOWLEDGE_TYPES.length);
    }, BRAIN_SCENARIO_ROTATION_MS / 2);
    return () => window.clearInterval(interval);
  }, [activeSection]);
  useEffect(() => {
    if (activeSection !== 'company-brain') return;
    const interval = window.setInterval(() => {
      setBrainVerbIndex(current => (current + 1) % brainVerbs.length);
    }, HEADLINE_ROTATION_MS);
    return () => window.clearInterval(interval);
  }, [activeSection, brainVerbs.length]);
  useEffect(() => {
    const sections = SECTIONS.map(section => document.getElementById(section.id)).filter((section): section is HTMLElement => Boolean(section));
    if (sections.length === 0) return;
    const observer = new IntersectionObserver(entries => {
      const scrollingDown = window.scrollY >= lastScrollYRef.current;
      lastScrollYRef.current = window.scrollY;
      const entering = entries.filter(entry => entry.isIntersecting).sort((a, b) => SECTIONS.findIndex(section => section.id === a.target.id) - SECTIONS.findIndex(section => section.id === b.target.id));
      const next = scrollingDown ? entering[entering.length - 1] : entering[0];
      if (next?.target.id) {
        setActiveSection(next.target.id as SectionId);
      }
    }, {
      rootMargin: '-32% 0px -67% 0px',
      threshold: 0
    });
    sections.forEach(section => observer.observe(section));
    return () => observer.disconnect();
  }, []);
  const scrollToSection = (sectionId: SectionId) => {
    const el = document.getElementById(sectionId);
    if (!el) return;
    const navHeight = navRef.current?.offsetHeight ?? 0;
    const headerOffset = 76;
    const top = el.getBoundingClientRect().top + window.scrollY - navHeight - headerOffset - 22;
    window.scrollTo({
      top,
      behavior: 'smooth'
    });
  };
  return <div className="min-h-screen pt-24 text-[#2B211A]" style={{
    backgroundColor: '#FAF8F4',
    backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='8' height='8' viewBox='0 0 8 8' xmlns='http://www.w3.org/2000/svg'%3E%3Crect width='2' height='2' fill='%232B211A' fill-opacity='0.10'/%3E%3C/svg%3E\")",
    backgroundSize: '8px 8px'
  }} data-id="element-5690" data-mp="1kr3llgayv22p">
      <section className="pb-12 pt-12 md:pb-16 md:pt-20" data-id="element-5691" data-mp="3f5z9dt4kk17p">
        <div className="mx-auto max-w-[1280px] px-6 md:px-10" data-id="element-5692" data-mp="3a7fl3m2i6dc6">
          <div className="mx-auto max-w-5xl text-center" data-id="element-5693" data-mp="3aa1ulr8yf2x1">
            <h1 className="font-title text-5xl font-bold leading-[0.95] tracking-tight text-[#2B211A] md:text-7xl" data-id="element-5694" data-mp="2miwdz9v6w6lo">
              The AI Platform for{' '}
              <span className="inline-flex overflow-hidden align-baseline" data-id="element-5695" data-mp="2vijhc17hbmhd">
                <AnimatePresence mode="wait" initial={false} data-id="element-5696" data-mp="2vgggem4sg8xv">
                  <motion.span key={audiencePhrases[audienceIndex]} initial={{
                  y: '100%',
                  opacity: 0,
                  rotateX: -60
                }} animate={{
                  y: 0,
                  opacity: 1,
                  rotateX: 0
                }} exit={{
                  y: '-100%',
                  opacity: 0,
                  rotateX: 60
                }} transition={{
                  duration: 0.42,
                  ease: [0.22, 1, 0.36, 1]
                }} className="block" data-id="element-5697" data-mp="2rgeq194i71xw">
                    {audiencePhrases[audienceIndex]}
                  </motion.span>
                </AnimatePresence>
              </span>
              <br data-id="element-5698" data-mp="2iz5oc92rqcxv" />
              that want to{' '}
              <span className="relative inline-flex overflow-hidden align-baseline" data-id="element-5699" data-mp="2j2jpxf3lyi49">
                <span className="absolute inset-x-0 bottom-[-4px] h-[9px] bg-[#EA580C]" aria-hidden="true" data-id="element-5700" data-mp="2o1d65faa4n84" />
                <AnimatePresence mode="wait" initial={false} data-id="element-5701" data-mp="2swiq48k4frtk">
                  <motion.span key={headlinePhrases[headlineIndex]} initial={{
                  y: '100%',
                  opacity: 0,
                  rotateX: -60
                }} animate={{
                  y: 0,
                  opacity: 1,
                  rotateX: 0
                }} exit={{
                  y: '-100%',
                  opacity: 0,
                  rotateX: 60
                }} transition={{
                  duration: 0.42,
                  ease: [0.22, 1, 0.36, 1]
                }} className="relative z-10 block" data-id="element-5702" data-mp="2xw55oonf9k5j">
                    {headlinePhrases[headlineIndex]}
                  </motion.span>
                </AnimatePresence>
              </span>
            </h1>
          </div>
        </div>
      </section>

      <div ref={navRef} className="sticky top-[72px] z-40" data-id="element-5703" data-mp="3vr8tg3athqvw">
        <nav className="mx-auto grid max-w-[1280px] grid-cols-3 gap-2 px-3 py-4 sm:gap-3 sm:px-6 md:flex md:justify-center md:px-10" aria-label="Section navigation" data-id="element-5704" data-mp="3vrqurecc6vao">
          {SECTIONS.map(section => {
          const isActive = activeSection === section.id;
          const Icon = section.icon;
          return <button key={section.id} type="button" onClick={() => scrollToSection(section.id)} className={`inline-flex min-w-0 items-center justify-center gap-1.5 rounded-full border px-2 py-2.5 text-[clamp(0.72rem,2.7vw,1rem)] font-semibold shadow-sm transition-all sm:gap-2 sm:px-4 md:shrink-0 md:px-5 md:text-lg ${isActive ? 'border-[#EA580C] bg-[#EA580C] text-white shadow-md hover:bg-[#C2410C]' : 'border-neutral-200 bg-white text-neutral-600 hover:border-[#EA580C]/30 hover:text-[#2B211A]'}`} aria-current={isActive ? 'true' : undefined} data-id="element-5705" data-mp="2famn4w0thrs0">
                <Icon size={19} className={`shrink-0 ${isActive ? 'text-white' : 'text-neutral-400'}`} data-id="element-5706" data-mp="2jkdi1zbqswui" />
                <span className="min-w-0 leading-tight" data-id="element-5707" data-mp="2emv8q7ttcflz">{section.label}</span>
              </button>;
        })}
        </nav>
      </div>

      <main data-id="element-5708" data-mp="2tikvi86towrr">
        <section id="co-worker" className="scroll-mt-40 border-b border-neutral-200/70 bg-[#FAF8F4] py-20 md:py-28" data-id="element-5709" data-mp="2th9pe1pwncxh">
          <div className="mx-auto grid max-w-[1280px] items-center gap-10 px-6 md:grid-cols-[1.2fr_0.8fr] md:px-10" data-id="element-5710" data-mp="z5bkgbymp37e">
            <SurfacePreview activeSurface={activeSurface} setActiveSurface={setActiveSurface} data-id="element-5711" data-mp="u6rsnuxcsx74" />

            <div data-id="element-5712" data-mp="35t24k5qux2gs">
              <h2 className="mb-5 font-title text-4xl font-bold leading-tight tracking-tight text-[#2B211A] md:text-5xl" data-id="element-5713" data-mp="35umzhwh32nf6">
                An AI Co Worker where your team already lives
              </h2>
              <SurfaceTabsControl activeSurface={activeSurface} setActiveSurface={setActiveSurface} className="mb-8 hidden md:flex" tabClassName="!px-4 !py-2.5 !text-base" data-id="element-5714" data-mp="2w2qvy5w8a53k" />

              <div className="space-y-4 font-title text-xl text-[#2B211A] md:text-2xl" style={{
              fontWeight: 500
            }} data-id="element-5715" data-mp="2n2knwzjatxbs">
                <IconBullet icon={PlugZap} label="Plugs into everything" data-id="element-5716" data-mp="2r68upg03wovz" />
                <IconBullet icon={Zap} label="Real work: Audits, analysis, reports, presentations" data-id="element-5717" data-mp="2d5y48e2plcbc" />
                <IconBullet icon={Sparkles} label="Autonomous & Proactive" data-id="element-5718" data-mp="2i52d13lq1815" />
              </div>
            </div>
          </div>
        </section>

        <section id="company-brain" className="scroll-mt-40 border-b border-neutral-200/70 bg-white py-20 md:py-28" data-id="element-5719" data-mp="bbqie7gxs1qm">
          <div className="mx-auto max-w-[1280px] px-6 md:px-10" data-id="element-5720" data-mp="ev7ghg8nzg6m">
            <div className="mx-auto max-w-4xl text-center" data-id="element-5721" data-mp="ews7wwzomj09">
              <h2 className="font-title text-4xl font-bold leading-tight text-[#2B211A] md:text-6xl" data-id="element-5722" data-mp="9vcp7bkz8ol1">
                A company brain that
                <br data-id="element-5723" data-mp="4zduz7cywccp" />
                <span className="inline-flex overflow-hidden align-baseline" data-id="element-5724" data-mp="3w3m2walj33v3">
                  <AnimatePresence mode="wait" initial={false} data-id="element-5725" data-mp="b20la9hwl3">
                    <motion.span key={brainVerbs[brainVerbIndex]} initial={{
                    y: '100%',
                    opacity: 0,
                    rotateX: -60
                  }} animate={{
                    y: 0,
                    opacity: 1,
                    rotateX: 0
                  }} exit={{
                    y: '-100%',
                    opacity: 0,
                    rotateX: 60
                  }} transition={{
                    duration: 0.42,
                    ease: [0.22, 1, 0.36, 1]
                  }} className="block" data-id="element-5726" data-mp="xknhs5s6bco3">
                      {brainVerbs[brainVerbIndex]}
                    </motion.span>
                  </AnimatePresence>
                </span>{' '}
                everything
              </h2>
            </div>

            <div className="relative mt-10 md:pt-24" data-id="element-5727" data-mp="1susofbnwmyrs">
              <CompanyBrainBranches data-id="element-5728" data-mp="1sxeybhy9pqd6" />
              <div className="grid items-start gap-6 md:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)]" data-id="element-5729" data-mp="1xuf3lqnamp38">
                <SeoConversationPanel side="left" scenario={BRAIN_SCENARIOS[0]} data-id="element-5730" data-mp="22u1gfyz7m3bi" />
                <motion.div animate={{
                opacity: 1
              }} transition={{
                duration: 0.42,
                ease: [0.22, 1, 0.36, 1]
              }} className="md:w-[190px]" data-id="element-5731" data-mp="26ubmfbn04tqd">
                  <CompanyBrainBridge activeKnowledge={KNOWLEDGE_TYPES[activeKnowledgeIndex]} onSelectKnowledge={selectBrainKnowledge} data-id="element-5732" data-mp="2gkwjuvqxvs7p" />
                </motion.div>
                <SeoConversationPanel side="right" scenario={BRAIN_SCENARIOS[brainScenarioIndex]} data-id="element-5733" data-mp="2qiu4l99lb6vk" />
              </div>
            </div>
          </div>
        </section>

        <section id="connected-apps" className="scroll-mt-40 bg-[#FAF8F4] py-20 md:py-28" data-id="element-5734" data-mp="3n8d2o04o2frf">
          <div className="mx-auto max-w-[1280px] px-6 md:px-10" data-id="element-5735" data-mp="3ial9z3w76pm6">
            <div className="mx-auto max-w-5xl text-center" data-id="element-5736" data-mp="uwee956wyzt">
              <h2 className="font-title text-4xl font-bold leading-tight text-[#2B211A] md:text-6xl" data-id="element-5737" data-mp="wfw846hvi3p">
                Connect tools once.
                <br data-id="element-5738" data-mp="34ajzqxu1m5u1" />
                <span className="text-[#EA580C]" data-id="element-5739" data-mp="34c4uangcxorw">Use them everywhere.</span>
              </h2>
              <p className="mt-5 text-lg font-medium text-[#6B5D52] md:text-xl" data-id="element-5740" data-mp="2zdk47gutklh6">
                Security built in.
              </p>
            </div>

            <ConnectedAppsHub data-id="element-5741" data-mp="39ag4x0eh83b6" />
            <ConnectedAppsBenefits data-id="element-5742" data-mp="39a6ljvnxsueg" />

            <div className="mt-20 md:mt-28" data-id="element-5743" data-mp="39950qzh4ctjj">
              <ConnectedAppsTable data-id="element-5744" data-mp="sl9jglvbdvms" />
            </div>

            <AccelerationControlSection data-id="element-5745" data-mp="sjzlrxsdipgo" />
          </div>
        </section>
      </main>
    </div>;
}
function SurfacePreview({
  activeSurface,
  setActiveSurface
}: {
  activeSurface: SurfaceKey;
  setActiveSurface: (surface: SurfaceKey) => void;
}) {
  const mobileTabs = <SurfaceTabsControl activeSurface={activeSurface} setActiveSurface={setActiveSurface} className="relative z-10 flex md:hidden" tabClassName="!flex-1 !justify-center !px-2 !py-2 !text-xs" quiet data-id="element-5746" data-mp="113uo0dil0tpf" />;
  if (activeSurface === 'chatgpt') {
    return <div className="relative mx-auto w-full max-w-[760px] rounded-[28px] bg-[#EA580C]/10 p-2 pt-1 md:bg-transparent md:p-0" data-id="element-5747" data-mp="r6gmrl58cbhp">
        <div className="absolute -inset-3 hidden rounded-[28px] bg-[#EA580C]/10 md:block" aria-hidden="true" data-id="element-5748" data-mp="1jtub4uuv3yq7" />
        {mobileTabs}
        <div className="relative rounded-[22px] rounded-tr-none bg-white p-3 text-left shadow-[0_28px_80px_rgba(43,33,26,0.13)] md:rounded-[24px]" data-id="element-5749" data-mp="153n193ix7o14">
          <MoreThanAnswers embedded data-id="element-5750" data-mp="1xt2d1vietky3" />
        </div>
      </div>;
  }
  if (activeSurface === 'code') {
    return <div className="relative mx-auto w-full max-w-[760px] rounded-[28px] bg-[#EA580C]/10 p-2 pt-1 md:bg-transparent md:p-0" data-id="element-5751" data-mp="22jiqhfm8d6kg">
        {mobileTabs}
        <CodeTerminal data-id="element-5752" data-mp="2cf95s6sdi1ib" />
      </div>;
  }
  return <div className="relative mx-auto w-full max-w-[760px] rounded-[28px] bg-[#EA580C]/10 p-2 pt-1 md:bg-transparent md:p-0" data-id="element-5753" data-mp="27gofaz2xaw72">
      <div className="absolute -inset-3 hidden rounded-[28px] bg-[#EA580C]/10 md:block" aria-hidden="true" data-id="element-5754" data-mp="23ebcaifk9cdz" />
      {mobileTabs}
      <div className="relative text-left [&>div]:rounded-tl-none md:[&>div]:rounded-tl-2xl" data-id="element-5755" data-mp="1yfhwukgprbb6">
        <SlackPanel data-id="element-5756" data-mp="1thor1sjbojbf" />
      </div>
    </div>;
}
function SurfaceTabsControl({
  activeSurface,
  setActiveSurface,
  className = '',
  tabClassName = '',
  quiet = false
}: {
  activeSurface: SurfaceKey;
  setActiveSurface: (surface: SurfaceKey) => void;
  className?: string;
  tabClassName?: string;
  quiet?: boolean;
}) {
  if (quiet) {
    const tabs = [{
      surface: 'slack' as const,
      label: 'Slack',
      icon: <TabIcon src="https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/slack.svg" fallback="S" data-id="element-5757" data-mp="1apnj80oklipv" />
    }, {
      surface: 'code' as const,
      label: 'Claude',
      icon: <TabIcon slug="claude" fallback="C" data-id="element-5758" data-mp="3hdwa3l137elf" />,
      ariaLabel: 'Claude'
    }, {
      surface: 'chatgpt' as const,
      label: 'ChatGPT',
      icon: <TabIcon src="https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/openai.svg" fallback="AI" data-id="element-5759" data-mp="3mfll50ob7s9i" />
    }];
    return <div className={`w-full items-center gap-0 ${className}`} role="tablist" aria-label="Chat surface" data-id="element-5760" data-mp="3ve7612h97jdg">
        {tabs.map(tab => {
        const active = activeSurface === tab.surface;
        return <button key={tab.surface} type="button" role="tab" aria-selected={active} aria-label={tab.ariaLabel} onClick={() => setActiveSurface(tab.surface)} className={`inline-flex items-center gap-1.5 rounded-t-[16px] border border-transparent px-2 py-2 text-xs text-[#2B211A] transition-colors ${active ? 'bg-white font-bold shadow-sm' : 'font-semibold text-[#6B5D52]'} ${tabClassName}`} data-id="element-5761" data-mp="33n190uwty1se">
              {tab.icon}
              {tab.label}
            </button>;
      })}
      </div>;
  }
  return <div className={`w-full items-center gap-2.5 md:gap-3 ${className}`} role="tablist" aria-label="Chat surface" data-id="element-5762" data-mp="1dbznddaasf65">
      <SurfaceTab surface="slack" activeSurface={activeSurface} setActiveSurface={setActiveSurface} className={tabClassName} data-id="element-5763" data-mp="18dej92rxdbcf">
        <TabIcon src="https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/slack.svg" fallback="S" data-id="element-5764" data-mp="1nax6g0oyfghr" />
        Slack
      </SurfaceTab>
      <SurfaceTab surface="code" activeSurface={activeSurface} setActiveSurface={setActiveSurface} ariaLabel="Claude" className={tabClassName} data-id="element-5765" data-mp="1idf6v0wtudmf">
        <TabIcon slug="claude" fallback="C" data-id="element-5766" data-mp="1rcipy4vzut0x" />
        Claude
      </SurfaceTab>
      <SurfaceTab surface="chatgpt" activeSurface={activeSurface} setActiveSurface={setActiveSurface} className={tabClassName} data-id="element-5767" data-mp="260djfnhtt1f2">
        <TabIcon src="https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/openai.svg" fallback="AI" data-id="element-5768" data-mp="212lhs1rdq0xo" />
        ChatGPT
      </SurfaceTab>
    </div>;
}
function SeoConversationPanel({
  side,
  scenario
}: {
  side: 'left' | 'right';
  scenario: (typeof BRAIN_SCENARIOS)[number];
}) {
  const isLeft = side === 'left';
  const prompt = isLeft ? scenario.leftPrompt : scenario.rightPrompt;
  const response = isLeft ? scenario.leftResponse : scenario.rightResponse;
  const [leftWorkingStep, setLeftWorkingStep] = useState(0);
  const [rightWorkingStep, setRightWorkingStep] = useState(0);
  const showRightWorking = !isLeft && rightWorkingStep < 2;
  const showResponse = isLeft ? leftWorkingStep >= LEFT_WORKING_STEPS.length - 1 : rightWorkingStep >= 2;
  useEffect(() => {
    if (!isLeft) return;
    setLeftWorkingStep(0);
    const timers = LEFT_WORKING_STEPS.slice(1).map((_, index) => window.setTimeout(() => setLeftWorkingStep(index + 1), (index + 1) * 900));
    return () => timers.forEach(timer => window.clearTimeout(timer));
  }, [isLeft]);
  useEffect(() => {
    if (isLeft) return;
    setRightWorkingStep(0);
    const searchTimer = window.setTimeout(() => setRightWorkingStep(1), 900);
    const doneTimer = window.setTimeout(() => setRightWorkingStep(2), 3900);
    return () => {
      window.clearTimeout(searchTimer);
      window.clearTimeout(doneTimer);
    };
  }, [isLeft, scenario.id]);
  return <div className="relative h-[390px] overflow-hidden rounded-[24px] border border-sand-200 bg-white p-4 text-left shadow-[0_28px_80px_rgba(43,33,26,0.13)] md:p-5" data-id="element-5769" data-mp="205ips1uny85b">
      <AnimatePresence mode="wait" data-id="element-5770" data-mp="1r2hoqfknn78i">
        <motion.div key={`${scenario.id}-${side}`} initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -8
      }} transition={{
        duration: 0.28,
        ease: [0.22, 1, 0.36, 1]
      }} data-id="element-5771" data-mp="1r3j95ank962w">
        <div className="h-[350px] space-y-5 overflow-hidden bg-white" data-id="element-5772" data-mp="252iyylmt7r0n">
          <div className="flex justify-end" data-id="element-5773" data-mp="2a1df1fte71h2">
            <div className="inline-flex max-w-[94%] items-start gap-2 rounded-full bg-[#F1F1F1] px-4 py-3" data-id="element-5774" data-mp="2a02ctkfli1s2">
              <GigaChatMark shape="square" data-id="element-5775" data-mp="2xpwohyo6hkaq" />
              <p className="pt-0.5 text-base font-medium leading-snug text-[#2B211A]" data-id="element-5776" data-mp="32ipwcvey18r1">
                Giga {prompt}
              </p>
            </div>
          </div>
          {(isLeft || showRightWorking) && <div className="relative flex items-center gap-2.5 overflow-hidden text-neutral-400" data-id="element-5777" data-mp="m3bao7wg2c2g">
              <GigaChatMark shape="circle" data-id="element-5778" data-mp="zupu2fu9kfa1" />
              <p className="relative text-sm font-medium leading-snug" data-id="element-5779" data-mp="zxvhlqn8v93n">
                {isLeft ? LEFT_WORKING_STEPS[leftWorkingStep] : rightWorkingStep === 0 ? 'Searching tracks' : 'Synthesizing shared context'}
              </p>
              {(isLeft ? leftWorkingStep < LEFT_WORKING_STEPS.length - 1 : true) && <motion.span aria-hidden="true" className="pointer-events-none absolute inset-y-0 w-16 bg-gradient-to-r from-transparent via-white/80 to-transparent" animate={{
              x: ['-120%', '520%']
            }} transition={{
              duration: 1.8,
              repeat: Infinity,
              ease: 'linear'
            }} data-id="element-5780" data-mp="37l407dkgri8" />}
            </div>}
          {showResponse && <motion.div initial={{
            opacity: 0,
            y: 6
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.24,
            ease: [0.22, 1, 0.36, 1]
          }} className="pl-9 text-sm leading-relaxed text-[#655B54]" data-id="element-5781" data-mp="1nku0sw2r0v59">
              <ResponseContent response={response} lead={isLeft ? 'Here’s what I saved to the company brain:' : 'Here’s what’s happening:'} data-id="element-5782" data-mp="u9f1aq2oz8qh" />
            </motion.div>}
        </div>
        </motion.div>
      </AnimatePresence>
      {isLeft && showResponse && <div className="pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-gradient-to-b from-white/0 via-white/90 to-white" aria-hidden="true" data-id="element-5783" data-mp="6ijgicwnldsq" />}
    </div>;
}
function GigaChatMark({
  shape
}: {
  shape: 'square' | 'circle';
}) {
  return <span className={`flex shrink-0 items-center justify-center bg-[#EA580C] ${shape === 'square' ? 'rounded-lg' : 'rounded-full'} ${shape === 'square' ? 'mt-0.5 size-5' : 'size-6'}`} data-id="element-5784" data-mp="gafc9hb9orts">
      <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/giga-mark.png" alt="" className={`${shape === 'square' ? 'size-3.5' : 'size-4'} [filter:brightness(0)_invert(1)]`} data-id="element-5785" data-mp="3jwmrf04vxluc" />
    </span>;
}
function ResponseContent({
  response,
  lead
}: {
  response: string | readonly string[];
  lead?: string;
}) {
  if (Array.isArray(response)) {
    return <>
        {lead && <p className="mb-2 text-[#2B211A]" data-id="element-5786" data-mp="2tfkga7xhrj9h">{lead}</p>}
        <ul className="space-y-1.5" data-id="element-5787" data-mp="2tgc8r9vsl0vj">
          {response.map(item => <li key={item} className="flex gap-2" data-id="element-5788" data-mp="2ooc4x6clpnto">
              <span className="mt-2 size-1.5 shrink-0 rounded-full bg-[#2B211A]" data-id="element-5789" data-mp="2onj5kqfz20lk" />
              <span data-id="element-5790" data-mp="2jqj1gl2on7x3">
                {item.includes(':') ? <>
                    <strong className="font-bold text-[#2B211A]" data-id="element-5791" data-mp="2er6dfx02rusx">{item.slice(0, item.indexOf(':') + 1)}</strong>
                    {item.slice(item.indexOf(':') + 1)}
                  </> : item}
              </span>
            </li>)}
        </ul>
      </>;
  }
  return <>
      {lead && <p className="mb-2 text-[#2B211A]" data-id="element-5792" data-mp="9mtpo4u0enrg">{lead}</p>}
      <p data-id="element-5793" data-mp="9o4rw07t3ngg">{response}</p>
    </>;
}
function CompanyBrainBranches() {
  return <div className="pointer-events-none absolute inset-x-0 top-0 hidden h-24 md:block" aria-hidden="true" data-id="element-5794" data-mp="doygkvt2p6ae">
      <svg className="absolute inset-0 size-full overflow-visible" viewBox="0 0 1200 96" preserveAspectRatio="none" fill="none" data-id="element-5795" data-mp="3w37q5j5zs13l">
        <motion.path d="M240 95 V24 H576" stroke="#EA580C" strokeWidth="3.5" strokeDasharray="4 8" strokeLinecap="round" animate={{
        strokeDashoffset: [0, -20]
      }} transition={{
        duration: 1.25,
        repeat: Infinity,
        ease: 'linear'
      }} data-id="element-5796" data-mp="4uk574bnyfwk" />
        <motion.path d="M960 95 V24 H624" stroke="#EA580C" strokeWidth="3.5" strokeDasharray="4 8" strokeLinecap="round" animate={{
        strokeDashoffset: [0, -20]
      }} transition={{
        duration: 1.25,
        repeat: Infinity,
        ease: 'linear'
      }} data-id="element-5797" data-mp="3d4lpu6iaue0q" />
        <motion.path d="M600 48 V95" stroke="#EA580C" strokeWidth="3.5" strokeDasharray="4 8" strokeLinecap="round" animate={{
        strokeDashoffset: [0, -20]
      }} transition={{
        duration: 1.25,
        repeat: Infinity,
        ease: 'linear'
      }} data-id="element-5798" data-mp="1gb0c2995jo7t" />
      </svg>
      <div className="absolute left-1/2 top-0 z-10 flex size-12 -translate-x-1/2 items-center justify-center rounded-xl bg-[#EA580C]" data-id="element-5799" data-mp="1leazz70spm35">
        <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/giga-mark.png" alt="" className="size-8 [filter:brightness(0)_invert(1)]" data-id="element-5800" data-mp="1ugk4ng9dul8m" />
      </div>
    </div>;
}
function CompanyBrainBridge({
  activeKnowledge,
  onSelectKnowledge
}: {
  activeKnowledge: string;
  onSelectKnowledge: (type: string) => void;
}) {
  return <div className="relative text-center md:min-h-[390px] md:pt-10" data-id="element-5801" data-mp="2e5umazsovg9d">
      <svg className="pointer-events-none absolute left-1/2 top-0 hidden h-[300px] w-4 -translate-x-1/2 md:block" viewBox="0 0 16 300" fill="none" aria-hidden="true" data-id="element-5802" data-mp="ns3s9o973dmk">
        <motion.path d="M8 0 V300" stroke="#EA580C" strokeWidth="3.5" strokeDasharray="4 8" strokeLinecap="round" animate={{
        strokeDashoffset: [0, -20]
      }} transition={{
        duration: 1.25,
        repeat: Infinity,
        ease: 'linear'
      }} data-id="element-5803" data-mp="jsv0goxptptn" />
      </svg>
      <div className="relative z-10 grid grid-cols-2 gap-2 text-left text-xs font-semibold sm:grid-cols-3 md:grid-cols-1 md:gap-3 md:text-sm" data-id="element-5804" data-mp="2ss4uk70ot4a8">
        {KNOWLEDGE_TYPES.map(type => {
        const active = activeKnowledge === type;
        const Icon = KNOWLEDGE_ICONS[type];
        return <button key={type} type="button" onClick={() => onSelectKnowledge(type)} className={`flex items-center gap-2 rounded-lg border bg-white px-3 py-2 text-[#2B211A] transition-colors ${active ? 'border-2 border-[#EA580C]' : 'border-neutral-200 hover:border-[#EA580C]/40'}`} data-id="element-5805" data-mp="36sfo5htdswz3">
              <Icon size={15} className={active ? 'text-[#EA580C]' : 'text-[#2B211A]'} data-id="element-5806" data-mp="3liwwll4fon5h" />
              {type}
            </button>;
      })}
      </div>
    </div>;
}
const CONNECTED_APP_BENEFITS = [{
  title: 'No tool left behind',
  copy: 'Connect your agents to virtually anything your team works with. Choose from 4,000 tools in our directory, or bring your own.',
  icon: PlugZap
}, {
  title: 'Up and running in minutes',
  copy: 'Point-and-click setup with no technical configuration or lengthy onboarding.',
  icon: Rocket
}, {
  title: 'Built to keep working',
  copy: 'We manage token refreshing and other maintenance, and send you a one-click "reconnect" link when something disconnects.',
  icon: RefreshCw
}, {
  title: 'Full Visibility',
  copy: "Unlike other tools, know what your team has connected, how it's being used, and what's being shared.",
  icon: Eye
}, {
  title: 'Keep your connections independent',
  copy: 'Store and manage credentials outside ChatGPT and Claude, so no single AI provider controls how your team accesses its tools.',
  icon: KeyRound
}] as const;
const ACCELERATION_CONTROL_GROUPS = [{
  heading: 'Go Faster',
  items: [{
    title: 'Offload busywork and associate work',
    copy: 'Hand Giga the repetitive research, formatting, follow-up, and first-draft work so your team can spend more time on judgment calls.'
  }, {
    title: 'Kill Meetings',
    copy: 'Turn scattered conversations into crisp updates, decisions, and next steps without adding another recurring sync to the calendar.'
  }, {
    title: 'Stop waiting for numbers',
    copy: 'Pull live metrics from the systems you already use and get answers, reports, and analysis without waiting on manual exports.'
  }]
}, {
  heading: 'Stay In Control',
  items: [{
    title: 'Own your own data',
    copy: 'Export your data, keep your history portable, and switch systems whenever you need without losing the work your team has built.'
  }, {
    title: 'Get Real Time Visibility',
    copy: 'See what is connected, what is being used, and where sensitive work is happening across your team as it happens.'
  }, {
    title: 'Built for teams',
    copy: 'Manage shared access, team workflows, and company context from one place instead of stitching together personal AI accounts.'
  }]
}] as const;
const CONNECTED_TOOLS = [{
  name: 'Salesforce',
  src: 'https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/salesforce.svg',
  fallback: 'S',
  method: 'MCP'
}, {
  name: 'PostgreSQL',
  slug: 'postgresql',
  fallback: 'P',
  method: 'Database'
}, {
  name: 'Stripe',
  slug: 'stripe',
  fallback: 'S',
  method: 'API key'
}, {
  name: 'HubSpot',
  slug: 'hubspot',
  fallback: 'H',
  method: 'API key'
}, {
  name: 'Linear',
  slug: 'linear',
  fallback: 'L',
  method: 'MCP'
}, {
  name: 'Snowflake',
  slug: 'snowflake',
  fallback: 'S',
  method: 'Database'
}, {
  name: 'Internal tools',
  fallback: 'I',
  method: 'API key'
}] as const;
const CONNECTED_USERS = [{
  name: 'Mary',
  app: 'ChatGPT',
  src: 'https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/openai.svg',
  fallback: 'O',
  avatar: "https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Frame_26.png"
}, {
  name: 'Tom',
  app: 'Slack',
  src: 'https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/slack.svg',
  fallback: 'S',
  avatar: "https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Frame_24.png"
}, {
  name: 'Tony',
  app: 'Claude Code',
  src: 'https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/claude.svg',
  fallback: 'C',
  avatar: "https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Frame_25.png"
}, {
  name: 'Priya',
  app: 'ChatGPT',
  src: 'https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/openai.svg',
  fallback: 'O',
  avatar: "https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Frame_23.png"
}, {
  name: 'Alex',
  app: 'Slack',
  src: 'https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/slack.svg',
  fallback: 'S',
  avatar: "https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Frame_27.png"
}, {
  name: 'Nina',
  app: 'Claude Code',
  src: 'https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/claude.svg',
  fallback: 'C',
  avatar: "https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Rectangle_(2).png"
}, {
  name: 'Jordan',
  app: 'ChatGPT',
  src: 'https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/openai.svg',
  fallback: 'O',
  avatar: "https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Rectangle_(3).png"
}] as const;
function ConnectedAppsHub() {
  const connectionYs = CONNECTED_TOOLS.map((_, index) => 44 + index * 72);
  return <div className="relative mx-auto mt-14 max-w-[1120px] md:mt-20" data-id="element-5807" data-mp="2a0g0ctiuazhr">
      <div className="relative hidden h-[505px] lg:block" data-id="element-5808" data-mp="3da29syerq3p1">
        <svg className="pointer-events-none absolute inset-0 size-full" viewBox="0 0 1120 505" fill="none" aria-hidden="true" data-id="element-5809" data-mp="38afveln7ehen">
          {connectionYs.map(y => <React.Fragment key={y} data-id="element-5810" data-mp="3mbigav2gplnz">
              <motion.path d={'M320 ' + y + ' H390'} stroke="#EA580C" strokeWidth="2.5" strokeDasharray="4 8" strokeLinecap="round" animate={{
            strokeDashoffset: [0, -24]
          }} transition={{
            duration: 1.6,
            repeat: Infinity,
            ease: 'linear'
          }} data-id="element-5811" data-mp="3hauj1rnn48lb" />
              <motion.path d={'M800 ' + y + ' H730'} stroke="#EA580C" strokeWidth="2.5" strokeDasharray="4 8" strokeLinecap="round" animate={{
            strokeDashoffset: [0, 24]
          }} transition={{
            duration: 1.6,
            repeat: Infinity,
            ease: 'linear'
          }} data-id="element-5812" data-mp="8usz0bj2t45q" />
            </React.Fragment>)}
          <motion.path d="M390 44 V476 M390 260 H470" stroke="#EA580C" strokeWidth="2.5" strokeDasharray="4 8" strokeLinecap="round" animate={{
          strokeDashoffset: [0, -24]
        }} transition={{
          duration: 1.6,
          repeat: Infinity,
          ease: 'linear'
        }} data-id="element-5813" data-mp="dvgo2rnqvaxb" />
          <motion.path d="M650 260 H730 M730 44 V476" stroke="#EA580C" strokeWidth="2.5" strokeDasharray="4 8" strokeLinecap="round" animate={{
          strokeDashoffset: [0, 24]
        }} transition={{
          duration: 1.6,
          repeat: Infinity,
          ease: 'linear'
        }} data-id="element-5814" data-mp="ngye8q07ggux" />
        </svg>

        {CONNECTED_TOOLS.map((tool, index) => <div key={tool.name} className="absolute left-[5%] w-[280px]" style={{
        top: 16 + index * 72
      }} data-id="element-5815" data-mp="jhza37maso4j" data-mpi={index}>
            <ConnectedToolNode tool={tool} data-id="element-5816" data-mp="9ptm08z62qum" />
          </div>)}

        <div className="absolute left-1/2 top-[260px] z-10 flex w-[180px] -translate-x-1/2 -translate-y-1/2 items-center justify-center gap-3 rounded-2xl bg-[#2B211A] px-5 py-5 text-white shadow-[0_24px_60px_-30px_rgba(43,33,26,0.8)]" data-id="element-5817" data-mp="9o8rukgrl9xa">
          <span className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-[#EA580C]" data-id="element-5818" data-mp="3ouwfcdp00fto">
            <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/giga-mark.png" alt="" className="size-7 [filter:brightness(0)_invert(1)]" data-id="element-5819" data-mp="3juqlqrz64j8y" />
          </span>
          <p className="font-title text-xl font-bold" data-id="element-5820" data-mp="3a5705ek2mbae">Vault</p>
        </div>

        {CONNECTED_USERS.map((user, index) => <div key={user.name} className="absolute right-[5%] w-[280px]" style={{
        top: 16 + index * 72
      }} data-id="element-5821" data-mp="361slosbs62z2" data-mpi={index}>
            <ConnectedUserNode user={user} data-id="element-5822" data-mp="2w5fq1owodf1o" />
          </div>)}
      </div>

      <div className="lg:hidden" data-id="element-5823" data-mp="2w7s77zulzldo">
        <div className="grid grid-cols-2 gap-2" data-id="element-5824" data-mp="2r74hdhi49al1">
          {CONNECTED_TOOLS.map(tool => <ConnectedToolNode key={tool.name} tool={tool} compact data-id="element-5825" data-mp="uecemxeynwg2" />)}
        </div>
        <HubFlowLine data-id="element-5826" data-mp="uc8bwisst3hd" />
        <div className="mx-auto flex w-fit items-center gap-3 rounded-2xl bg-[#2B211A] px-5 py-4 text-white" data-id="element-5827" data-mp="pf83w2ce1qnm">
          <span className="flex size-10 items-center justify-center rounded-xl bg-[#EA580C]" data-id="element-5828" data-mp="xws0vzhumkda">
            <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/giga-mark.png" alt="" className="size-6 [filter:brightness(0)_invert(1)]" data-id="element-5829" data-mp="11xlpkv348378" />
          </span>
          <p className="font-title text-lg font-bold" data-id="element-5830" data-mp="1bn58u1uqpz2m">Vault</p>
        </div>
        <HubFlowLine data-id="element-5831" data-mp="eyfqkaf7b4g4" />
        <div className="grid grid-cols-2 gap-2" data-id="element-5832" data-mp="eyyyqzf1uegy">
          {CONNECTED_USERS.map(user => <ConnectedUserNode key={user.name} user={user} compact data-id="element-5833" data-mp="j00x7e156mhv" />)}
        </div>
      </div>
    </div>;
}
function ConnectedToolNode({
  tool,
  compact = false
}: {
  tool: (typeof CONNECTED_TOOLS)[number];
  compact?: boolean;
}) {
  return <div aria-label={tool.name + ' connected through ' + tool.method} className={compact ? 'relative z-10 flex min-h-[92px] flex-col items-center justify-center gap-2 rounded-xl border border-neutral-200 bg-white px-2 text-center shadow-[0_18px_40px_-32px_rgba(43,33,26,0.7)]' : 'relative z-10 flex min-h-[56px] items-center gap-3 rounded-xl border border-neutral-200 bg-white px-4 shadow-[0_18px_40px_-32px_rgba(43,33,26,0.7)]'} data-id="element-5834" data-mp="sv2mc9vf3okg">
      <div className={compact ? 'flex items-center gap-2' : 'contents'} data-id="element-5835" data-mp="1om2yoixynh2e">
        <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-[#FAF8F4] text-[#EA580C]" data-id="element-5836" data-mp="1ok0c68w0o62f">
          {'src' in tool ? <TabIcon src={tool.src} fallback={tool.fallback} data-id="element-5837" data-mp="vx5utkuhxmt6" /> : 'slug' in tool ? <TabIcon slug={tool.slug} fallback={tool.fallback} data-id="element-5838" data-mp="vvcipq146ads" /> : <PlugZap size={16} data-id="element-5839" data-mp="qzx8r9688ymn" />}
        </span>
        <span className="min-w-0 flex-1 truncate text-sm font-semibold text-[#2B211A]" data-id="element-5840" data-mp="qwrme1ozgaum">{tool.name}</span>
      </div>
      <ConnectionMethodBadge method={tool.method} data-id="element-5841" data-mp="10x1xngrgj875" />
    </div>;
}
function ConnectionMethodBadge({
  method
}: {
  method: (typeof CONNECTED_TOOLS)[number]['method'];
}) {
  const config = {
    MCP: {
      icon: PlugZap,
      className: 'border-emerald-200 bg-emerald-50 text-emerald-700'
    },
    Database: {
      icon: Database,
      className: 'border-sky-200 bg-sky-50 text-sky-700'
    },
    'API key': {
      icon: KeyRound,
      className: 'border-amber-200 bg-amber-50 text-amber-700'
    }
  }[method];
  const MethodIcon = config.icon;
  return <span className={`inline-flex shrink-0 items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-bold ${config.className}`} data-id="element-5842" data-mp="29z3jjq3rd9pt">
      <MethodIcon size={12} strokeWidth={2.4} data-id="element-5843" data-mp="32m7jhgnzuxuq" />
      {method}
    </span>;
}
function ConnectedUserNode({
  user,
  compact = false
}: {
  user: (typeof CONNECTED_USERS)[number];
  compact?: boolean;
}) {
  return <div className={compact ? 'relative z-10 flex min-h-[92px] items-center gap-2 rounded-xl border border-neutral-200 bg-white px-3 shadow-[0_18px_40px_-32px_rgba(43,33,26,0.7)]' : 'relative z-10 flex min-h-[56px] items-center gap-3 rounded-xl border border-neutral-200 bg-white px-4 shadow-[0_18px_40px_-32px_rgba(43,33,26,0.7)]'} data-id="element-5844" data-mp="2tlsvg9jnb4nj">
      <span className="flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#FAF8F4]" data-id="element-5845" data-mp="1m9991f6wvx8q">
        <img src={user.avatar} alt="" className="size-full object-cover" data-id="element-5846" data-mp="2dt69fkde5thy" />
      </span>
      <div className="min-w-0 flex-1" data-id="element-5847" data-mp="28umptqm9stsr">
        <p className="truncate text-sm font-bold text-[#2B211A]" data-id="element-5848" data-mp="24ub3m9e9elf6">{user.name}</p>
      </div>
      <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-[#FAF8F4]" data-id="element-5849" data-mp="1zvhpcer5eqdy">
        <TabIcon src={user.src} fallback={user.fallback} data-id="element-5850" data-mp="2wlqz6sxsnihj" />
      </span>
    </div>;
}
function HubFlowLine() {
  return <div className="relative mx-auto h-10 w-3 overflow-hidden" aria-hidden="true" data-id="element-5851" data-mp="2skf96mb0cv8t">
      <motion.div className="absolute left-1/2 top-0 h-16 -translate-x-1/2 border-l-[3px] border-dashed border-[#EA580C]" animate={{
      y: [-16, 0]
    }} transition={{
      duration: 1.2,
      repeat: Infinity,
      ease: 'linear'
    }} data-id="element-5852" data-mp="2ntgo79f2r8z8" />
    </div>;
}
function ConnectedAppsBenefits() {
  return <div className="mt-16 border-y border-neutral-200 md:mt-20" data-id="element-5853" data-mp="3bj193xusggb5">
      {CONNECTED_APP_BENEFITS.map(({
      title,
      copy,
      icon: Icon
    }, index) => <motion.article key={title} initial={{
      opacity: 0,
      y: 18
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true,
      margin: '-80px'
    }} transition={{
      duration: 0.42,
      delay: index * 0.03,
      ease: [0.22, 1, 0.36, 1]
    }} className="grid gap-4 border-b border-neutral-200 py-8 last:border-b-0 md:grid-cols-[56px_minmax(0,0.9fr)_minmax(0,1.1fr)] md:items-center md:gap-8 md:py-10" data-id="element-5854" data-mp="3ivh6vwnn1qz9" data-mpi={index}>
          <span className="flex size-11 items-center justify-center rounded-xl border border-[#EA580C]/25 bg-white text-[#EA580C]" data-id="element-5855" data-mp="amh35fkp99kq">
            <Icon size={21} data-id="element-5856" data-mp="feicotqhgm72" />
          </span>
          <h3 className="font-title text-2xl font-bold leading-tight text-[#2B211A] md:text-3xl" data-id="element-5857" data-mp="ffte4mwghhv0">{title}</h3>
          <p className="max-w-xl text-base leading-7 text-[#6B5D52] md:text-lg md:leading-8" data-id="element-5858" data-mp="oe5ehgn4ju0n">{copy}</p>
        </motion.article>)}
    </div>;
}
function AccelerationControlSection() {
  return <div className="mt-20 md:mt-28" data-id="element-5859" data-mp="tavrfv3mu9hu">
      {ACCELERATION_CONTROL_GROUPS.map((group, groupIndex) => <section key={group.heading} className={groupIndex > 0 ? 'mt-16 md:mt-20' : undefined} data-id="element-5860" data-mp="1ybtuwedj8ivv" data-mpi={groupIndex}>
          <h1 className="font-title text-4xl font-bold leading-tight text-[#2B211A] md:text-6xl" data-id="element-5861" data-mp="23awql87mhcr6">
            {group.heading}
          </h1>
          <div className="mt-8 border-y border-neutral-200" data-id="element-5862" data-mp="2d3c67yvg5p8w">
            {group.items.map((item, index) => <motion.article key={item.title} initial={{
          opacity: 0,
          y: 18
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-80px'
        }} transition={{
          duration: 0.42,
          delay: index * 0.03,
          ease: [0.22, 1, 0.36, 1]
        }} className="grid gap-4 border-b border-neutral-200 py-8 last:border-b-0 md:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] md:items-center md:gap-8 md:py-10" data-id="element-5863" data-mp="1ffk8n3sh8m0s" data-mpi={index}>
                <h2 className="font-title text-2xl font-bold leading-tight text-[#2B211A] md:text-3xl" data-id="element-5864" data-mp="35qouqel6474u">{item.title}</h2>
                <p className="max-w-xl text-base leading-7 text-[#6B5D52] md:text-lg md:leading-8" data-id="element-5865" data-mp="u2titymkzqpx">{item.copy}</p>
              </motion.article>)}
          </div>
        </section>)}
    </div>;
}
type ConnectedAppsValue = boolean | 'Enterprise only' | 'Coming soon' | null;
const CONNECTED_APPS_ROWS: Array<{
  feature: string;
  description: string;
  chatgpt: ConnectedAppsValue;
  giga: ConnectedAppsValue;
}> = [{
  feature: 'Supports MCP connections',
  description: 'Connect through existing MCP servers from supported providers.',
  chatgpt: true,
  giga: true
}, {
  feature: 'Supports API connections',
  description: 'Stop having your team paste API tokens into conversations in plain text.',
  chatgpt: false,
  giga: true
}, {
  feature: 'Supports SQL connections',
  description: 'See which databases your team connects to, review their permissions, and flag anything that looks dangerous.',
  chatgpt: false,
  giga: true
}, {
  feature: 'Supports email/password connections',
  description: "Use Giga's cloud browser for tools without an API or MCP, so your agent can drive the browser and finish the work.",
  chatgpt: false,
  giga: true
}, {
  feature: 'Supports unlisted tools',
  description: 'Use the on-demand integration wizard to add tools that are not yet in our directory.',
  chatgpt: false,
  giga: true
}, {
  feature: 'Supports multiple accounts',
  description: 'Connect personal, team, client, and environment accounts without repeatedly signing in and out.',
  chatgpt: false,
  giga: true
}, {
  feature: "Gives admins full visibility on the team's connections",
  description: 'Give admins one view of every connection, its owner, permissions, and recent use.',
  chatgpt: false,
  giga: true
}, {
  feature: 'See what tools employees have connected',
  description: 'Understand which tools employees have authorized and where company data can flow.',
  chatgpt: 'Enterprise only',
  giga: true
}, {
  feature: 'Bring your own OAuth providers',
  description: 'Use your own OAuth app and consent screen when you need complete control over authorization.',
  chatgpt: null,
  giga: true
}, {
  feature: 'Audit activity',
  description: 'Review how connections are used, by whom, and when, with detailed activity history.',
  chatgpt: null,
  giga: 'Coming soon'
}];
function ConnectedAppsTable() {
  return <div className="overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-[0_30px_80px_-48px_rgba(43,33,26,0.4)]" data-id="element-5866" data-mp="1am79jc3kzalv">
      <div className="overflow-x-auto" data-id="element-5867" data-mp="i06403fvm71m">
        <table className="w-full min-w-[760px] border-collapse text-left" data-id="element-5868" data-mp="i17f27ww6tjc">
          <thead data-id="element-5869" data-mp="vrazctc7od65">
            <tr className="border-b border-neutral-200" data-id="element-5870" data-mp="vruc7vn08bdb">
              <th scope="col" className="w-[54%] px-6 py-6 md:px-8" data-id="element-5871" data-mp="r0ko4zh3tg3j">
                <span className="sr-only" data-id="element-5872" data-mp="qy832dg1upm9">Feature</span>
              </th>
              <th scope="col" className="w-[19%] px-5 py-6 font-title text-xl font-bold text-[#2B211A] md:text-2xl" data-id="element-5873" data-mp="397035pzk6od">
                ChatGPT
              </th>
              <th scope="col" className="w-[27%] bg-[#FFF8F3] px-5 py-6 font-title text-xl font-bold text-[#2B211A] md:text-2xl" data-id="element-5874" data-mp="byw0y8gqavjl">
                Giga
              </th>
            </tr>
          </thead>
          <tbody data-id="element-5875" data-mp="lw1vxrsgr6p3">
            {CONNECTED_APPS_ROWS.map(row => <tr key={row.feature} className="border-b border-neutral-100 last:border-b-0" data-id="element-5876" data-mp="lugxvs6xx5mh">
                <th scope="row" className="px-6 py-5 text-base font-medium leading-6 text-[#2B211A] md:px-8 md:text-lg" data-id="element-5877" data-mp="qol6efvutlpo">
                  <span className="block" data-id="element-5878" data-mp="vopqsgzvv0l6">{row.feature}</span>
                  <span className="mt-1.5 block max-w-2xl text-sm font-normal leading-5 text-[#8A7B70]" data-id="element-5879" data-mp="zmdlbixnyrcj">
                    {row.description}
                  </span>
                </th>
                <td className="px-5 py-5 text-[#2B211A]" data-id="element-5880" data-mp="19ktersk5ycp7">
                  <ComparisonValue value={row.chatgpt} data-id="element-5881" data-mp="36eo5oct9bf6e" />
                </td>
                <td className="bg-[#FFF8F3] px-5 py-5 text-[#2B211A]" data-id="element-5882" data-mp="36giptowpf39f">
                  <ComparisonValue value={row.giga} highlight data-id="element-5883" data-mp="3be13fslo26nr" />
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>
    </div>;
}
function ComparisonValue({
  value,
  highlight = false
}: {
  value: ConnectedAppsValue;
  highlight?: boolean;
}) {
  if (value === true) {
    return <span className={`inline-flex items-center ${highlight ? 'text-[#EA580C]' : 'text-[#2B211A]'}`} aria-label="Supported" data-id="element-5884" data-mp="2u4oufgbj9vre">
        <Check size={25} strokeWidth={2.6} aria-hidden="true" data-id="element-5885" data-mp="2p3969fe2794s" />
      </span>;
  }
  if (value === false) {
    return <span className="inline-flex items-center text-neutral-300" aria-label="Not supported" data-id="element-5886" data-mp="3htrgd0d7aarp">
        <X size={25} strokeWidth={2.4} aria-hidden="true" data-id="element-5887" data-mp="3cuoajdpec0io" />
      </span>;
  }
  if (value === null) {
    return <span aria-label="Not available" className="text-neutral-300" data-id="element-5888" data-mp="37vtv8lqn0u3b">—</span>;
  }
  return <span className={`text-sm font-semibold ${highlight ? 'text-[#EA580C]' : 'text-[#6B5D52]'}`} data-id="element-5889" data-mp="37xxuurhi76xs">{value}</span>;
}
function IconBullet({
  icon: Icon,
  label
}: {
  icon: React.ElementType;
  label: string;
}) {
  return <p className="flex items-center gap-3" data-id="element-5890" data-mp="oh8yigubg4yk">
      <span className="flex size-6 shrink-0 items-center justify-center text-[#EA580C]" data-id="element-5891" data-mp="ohhbe8qfva9r">
        <Icon size={22} strokeWidth={2.4} data-id="element-5892" data-mp="sh9nuwiye0ke" />
      </span>
      {label}
    </p>;
}